import * as THREE from 'three';
import type { BoxColliderDef, CylinderColliderDef, SphereColliderDef } from './colliderTypes';

export interface CollisionResult {
  position: THREE.Vector3;
  normal: THREE.Vector3 | null;
  collided: boolean;
}

/** Small offset applied after push-out to prevent floating-point boundary oscillation. */
const PUSH_EPSILON = 1e-5;

// Module-level scratch objects used by OBB box paths (resolveBoxCollision +
// rayIntersectsBox). Single-threaded JS — safe to share.
const _scratchEuler = new THREE.Euler();
const _scratchQuat = new THREE.Quaternion();
const _scratchInvQuat = new THREE.Quaternion();
const _scratchLocalPos = new THREE.Vector3();
const _scratchLocalResult = new THREE.Vector3();
const _scratchBoxLocalOrigin = new THREE.Vector3();
const _scratchBoxLocalDir = new THREE.Vector3();
const _scratchBoxAABBMin = new THREE.Vector3();
const _scratchBoxAABBMax = new THREE.Vector3();

/**
 * Resolve collision for a box collider — dispatches by mode.
 * - `solid`: камера остаётся СНАРУЖИ (push out from inside).
 * - `hollow`: камера остаётся ВНУТРИ (clamp from outside). AABB only —
 *   `rotation` игнорируется для hollow.
 */
export const resolveBoxCollision = (
  pos: THREE.Vector3,
  box: BoxColliderDef,
  cameraRadius: number,
  out: CollisionResult,
): CollisionResult => {
  if (box.mode === 'hollow') {
    return resolveBoxHollowCollision(pos, box, cameraRadius, out);
  }
  if (box.rotation) {
    return resolveOBBCollision(pos, box, cameraRadius, out);
  }

  const halfX = box.size.x / 2 + cameraRadius;
  const halfY = box.size.y / 2 + cameraRadius;
  const halfZ = box.size.z / 2 + cameraRadius;

  const minX = box.position.x - halfX;
  const maxX = box.position.x + halfX;
  const minY = box.position.y - halfY;
  const maxY = box.position.y + halfY;
  const minZ = box.position.z - halfZ;
  const maxZ = box.position.z + halfZ;

  // Check if point is inside the inflated AABB
  if (
    pos.x > minX && pos.x < maxX &&
    pos.y > minY && pos.y < maxY &&
    pos.z > minZ && pos.z < maxZ
  ) {
    // Find the axis with minimum penetration and push out
    const dists = [
      pos.x - minX, // push -X
      maxX - pos.x, // push +X
      pos.y - minY, // push -Y
      maxY - pos.y, // push +Y
      pos.z - minZ, // push -Z
      maxZ - pos.z, // push +Z
    ];

    let minDist = dists[0];
    let minIdx = 0;
    for (let i = 1; i < 6; i++) {
      if (dists[i] < minDist) {
        minDist = dists[i];
        minIdx = i;
      }
    }

    out.position.copy(pos);
    out.normal = out.normal ?? new THREE.Vector3();

    switch (minIdx) {
      case 0: out.position.x = minX - PUSH_EPSILON; out.normal.set(-1, 0, 0); break;
      case 1: out.position.x = maxX + PUSH_EPSILON; out.normal.set(1, 0, 0); break;
      case 2: out.position.y = minY - PUSH_EPSILON; out.normal.set(0, -1, 0); break;
      case 3: out.position.y = maxY + PUSH_EPSILON; out.normal.set(0, 1, 0); break;
      case 4: out.position.z = minZ - PUSH_EPSILON; out.normal.set(0, 0, -1); break;
      case 5: out.position.z = maxZ + PUSH_EPSILON; out.normal.set(0, 0, 1); break;
    }

    out.collided = true;
    return out;
  }

  out.position.copy(pos);
  out.normal = null;
  out.collided = false;
  return out;
};

/**
 * OBB push-out: transform pos to box local space, do the same axis-aligned
 * minimum-penetration push, transform corrected position + normal back.
 * Note: corner/edge inflation is approximate (faces only) — same approximation
 * the AABB path makes; acceptable for dev colliders with small cameraRadius
 * relative to box size.
 */
const resolveOBBCollision = (
  pos: THREE.Vector3,
  box: BoxColliderDef,
  cameraRadius: number,
  out: CollisionResult,
): CollisionResult => {
  const rot = box.rotation!;
  _scratchEuler.set(rot.x, rot.y, rot.z);
  _scratchQuat.setFromEuler(_scratchEuler);
  _scratchInvQuat.copy(_scratchQuat).invert();

  _scratchLocalPos.set(
    pos.x - box.position.x,
    pos.y - box.position.y,
    pos.z - box.position.z,
  ).applyQuaternion(_scratchInvQuat);

  const halfX = box.size.x / 2 + cameraRadius;
  const halfY = box.size.y / 2 + cameraRadius;
  const halfZ = box.size.z / 2 + cameraRadius;

  if (
    _scratchLocalPos.x > -halfX && _scratchLocalPos.x < halfX &&
    _scratchLocalPos.y > -halfY && _scratchLocalPos.y < halfY &&
    _scratchLocalPos.z > -halfZ && _scratchLocalPos.z < halfZ
  ) {
    const dists = [
      _scratchLocalPos.x + halfX, // push -X
      halfX - _scratchLocalPos.x, // push +X
      _scratchLocalPos.y + halfY, // push -Y
      halfY - _scratchLocalPos.y, // push +Y
      _scratchLocalPos.z + halfZ, // push -Z
      halfZ - _scratchLocalPos.z, // push +Z
    ];

    let minDist = dists[0];
    let minIdx = 0;
    for (let i = 1; i < 6; i++) {
      if (dists[i] < minDist) {
        minDist = dists[i];
        minIdx = i;
      }
    }

    _scratchLocalResult.copy(_scratchLocalPos);
    out.normal = out.normal ?? new THREE.Vector3();

    switch (minIdx) {
      case 0: _scratchLocalResult.x = -halfX - PUSH_EPSILON; out.normal.set(-1, 0, 0); break;
      case 1: _scratchLocalResult.x = halfX + PUSH_EPSILON; out.normal.set(1, 0, 0); break;
      case 2: _scratchLocalResult.y = -halfY - PUSH_EPSILON; out.normal.set(0, -1, 0); break;
      case 3: _scratchLocalResult.y = halfY + PUSH_EPSILON; out.normal.set(0, 1, 0); break;
      case 4: _scratchLocalResult.z = -halfZ - PUSH_EPSILON; out.normal.set(0, 0, -1); break;
      case 5: _scratchLocalResult.z = halfZ + PUSH_EPSILON; out.normal.set(0, 0, 1); break;
    }

    // Transform back to world: rotate by quaternion, translate by box.position
    _scratchLocalResult.applyQuaternion(_scratchQuat);
    out.position.set(
      _scratchLocalResult.x + box.position.x,
      _scratchLocalResult.y + box.position.y,
      _scratchLocalResult.z + box.position.z,
    );
    out.normal.applyQuaternion(_scratchQuat);
    out.collided = true;
    return out;
  }

  out.position.copy(pos);
  out.normal = null;
  out.collided = false;
  return out;
};

/**
 * Resolve collision for a hollow box (камера должна остаться ВНУТРИ).
 * AABB only — `box.rotation` игнорируется (закрытое помещение в локальной
 * системе модели обычно осе-выровнено; rotation добавлять при необходимости).
 *
 * Алгоритм: effective half-extents = size/2 - cameraRadius (камера держится
 * cameraRadius от каждой стены). Если pos выходит за этот ужатый куб на
 * какой-либо оси — клампим к границе, выставляем нормаль внутрь.
 */
const resolveBoxHollowCollision = (
  pos: THREE.Vector3,
  box: BoxColliderDef,
  cameraRadius: number,
  out: CollisionResult,
): CollisionResult => {
  const halfX = box.size.x / 2 - cameraRadius;
  const halfY = box.size.y / 2 - cameraRadius;
  const halfZ = box.size.z / 2 - cameraRadius;

  const minX = box.position.x - halfX;
  const maxX = box.position.x + halfX;
  const minY = box.position.y - halfY;
  const maxY = box.position.y + halfY;
  const minZ = box.position.z - halfZ;
  const maxZ = box.position.z + halfZ;

  out.position.copy(pos);
  let collided = false;
  let nx = 0;
  let ny = 0;
  let nz = 0;

  if (pos.x < minX) {
    out.position.x = minX + PUSH_EPSILON;
    nx = 1;
    collided = true;
  } else if (pos.x > maxX) {
    out.position.x = maxX - PUSH_EPSILON;
    nx = -1;
    collided = true;
  }

  if (pos.y < minY) {
    out.position.y = minY + PUSH_EPSILON;
    ny = 1;
    collided = true;
  } else if (pos.y > maxY) {
    out.position.y = maxY - PUSH_EPSILON;
    ny = -1;
    collided = true;
  }

  if (pos.z < minZ) {
    out.position.z = minZ + PUSH_EPSILON;
    nz = 1;
    collided = true;
  } else if (pos.z > maxZ) {
    out.position.z = maxZ - PUSH_EPSILON;
    nz = -1;
    collided = true;
  }

  if (collided) {
    out.normal = out.normal ?? new THREE.Vector3();
    out.normal.set(nx, ny, nz).normalize();
  } else {
    out.normal = null;
  }

  out.collided = collided;
  return out;
};

/**
 * Ray-box intersection. Handles both AABB (box.rotation undefined) and OBB
 * (box.rotation set). For OBB, ray is transformed into box-local space and
 * tested against an axis-aligned box centered at origin.
 */
export const rayIntersectsBox = (
  origin: THREE.Vector3,
  direction: THREE.Vector3,
  box: BoxColliderDef,
  maxT: number,
): number | null => {
  const halfX = box.size.x / 2;
  const halfY = box.size.y / 2;
  const halfZ = box.size.z / 2;

  if (box.rotation) {
    _scratchEuler.set(box.rotation.x, box.rotation.y, box.rotation.z);
    _scratchQuat.setFromEuler(_scratchEuler);
    _scratchInvQuat.copy(_scratchQuat).invert();

    _scratchBoxLocalOrigin.set(
      origin.x - box.position.x,
      origin.y - box.position.y,
      origin.z - box.position.z,
    ).applyQuaternion(_scratchInvQuat);
    _scratchBoxLocalDir.copy(direction).applyQuaternion(_scratchInvQuat);

    _scratchBoxAABBMin.set(-halfX, -halfY, -halfZ);
    _scratchBoxAABBMax.set(halfX, halfY, halfZ);

    return rayIntersectsAABB(
      _scratchBoxLocalOrigin,
      _scratchBoxLocalDir,
      _scratchBoxAABBMin,
      _scratchBoxAABBMax,
      maxT,
    );
  }

  _scratchBoxAABBMin.set(box.position.x - halfX, box.position.y - halfY, box.position.z - halfZ);
  _scratchBoxAABBMax.set(box.position.x + halfX, box.position.y + halfY, box.position.z + halfZ);
  return rayIntersectsAABB(origin, direction, _scratchBoxAABBMin, _scratchBoxAABBMax, maxT);
};

/**
 * Resolve collision for a hollow cylinder (camera must stay INSIDE).
 * Works in XZ plane for radial constraint + Y axis for height constraint.
 */
export const resolveCylinderCollision = (
  pos: THREE.Vector3,
  cyl: CylinderColliderDef,
  cameraRadius: number,
  out: CollisionResult,
): CollisionResult => {
  const effectiveRadius = cyl.radius - cameraRadius;
  const halfH = cyl.height / 2;
  const yMin = cyl.position.y - halfH + cameraRadius;
  const yMax = cyl.position.y + halfH - cameraRadius;

  let collided = false;
  out.position.copy(pos);
  out.normal = null;

  // Radial constraint in XZ plane
  const dx = pos.x - cyl.position.x;
  const dz = pos.z - cyl.position.z;
  const distXZ = Math.sqrt(dx * dx + dz * dz);

  if (distXZ > effectiveRadius && effectiveRadius > 0) {
    const invDist = 1 / distXZ;
    const nx = dx * invDist;
    const nz = dz * invDist;
    const clampedRadius = effectiveRadius - PUSH_EPSILON;
    out.position.x = cyl.position.x + nx * clampedRadius;
    out.position.z = cyl.position.z + nz * clampedRadius;
    out.normal = out.normal ?? new THREE.Vector3();
    out.normal.set(-nx, 0, -nz); // points inward
    collided = true;
  }

  // Height constraint
  if (out.position.y < yMin) {
    out.position.y = yMin + PUSH_EPSILON;
    out.normal = out.normal ?? new THREE.Vector3();
    out.normal.set(0, 1, 0);
    collided = true;
  } else if (out.position.y > yMax) {
    out.position.y = yMax - PUSH_EPSILON;
    out.normal = out.normal ?? new THREE.Vector3();
    out.normal.set(0, -1, 0);
    collided = true;
  }

  out.collided = collided;
  return out;
};

/**
 * Ray-AABB intersection using slab method.
 * Returns parametric t of first intersection, or null if no hit.
 * Only considers hits in [0, maxT] range.
 */
export const rayIntersectsAABB = (
  origin: THREE.Vector3,
  direction: THREE.Vector3,
  min: THREE.Vector3,
  max: THREE.Vector3,
  maxT: number,
): number | null => {
  let tMin = 0;
  let tMax = maxT;

  for (let i = 0; i < 3; i++) {
    const axis = i === 0 ? 'x' : i === 1 ? 'y' : 'z';
    const o = origin[axis];
    const d = direction[axis];
    const lo = min[axis];
    const hi = max[axis];

    if (Math.abs(d) < 1e-10) {
      // Ray parallel to slab — no hit if origin outside
      if (o < lo || o > hi) {
        return null;
      }
      continue;
    }

    let t1 = (lo - o) / d;
    let t2 = (hi - o) / d;

    if (t1 > t2) {
      const tmp = t1;
      t1 = t2;
      t2 = tmp;
    }

    tMin = Math.max(tMin, t1);
    tMax = Math.min(tMax, t2);

    if (tMin > tMax) {
      return null;
    }
  }

  return tMin;
};

/**
 * Ray-cylinder intersection for a vertical cylinder (Y axis).
 * Tests radial intersection in XZ plane, then checks Y bounds.
 * Returns parametric t of first intersection, or null if no hit.
 */
export const rayIntersectsCylinder = (
  origin: THREE.Vector3,
  direction: THREE.Vector3,
  center: THREE.Vector3,
  radius: number,
  halfHeight: number,
  maxT: number,
): number | null => {
  // Solve in XZ: |origin_xz + t * dir_xz - center_xz|^2 = radius^2
  const ox = origin.x - center.x;
  const oz = origin.z - center.z;
  const dx = direction.x;
  const dz = direction.z;

  const a = dx * dx + dz * dz;
  const b = 2 * (ox * dx + oz * dz);
  const c = ox * ox + oz * oz - radius * radius;

  if (Math.abs(a) < 1e-10) {
    // Ray is vertical — check if already inside/outside
    return null;
  }

  const discriminant = b * b - 4 * a * c;
  if (discriminant < 0) {
    return null;
  }

  const sqrtD = Math.sqrt(discriminant);
  const t1 = (-b - sqrtD) / (2 * a);
  const t2 = (-b + sqrtD) / (2 * a);

  // Check both intersections
  for (const t of [t1, t2]) {
    if (t < 0 || t > maxT) {
      continue;
    }
    const hitY = origin.y + t * direction.y;
    if (hitY >= center.y - halfHeight && hitY <= center.y + halfHeight) {
      return t;
    }
  }

  return null;
};

/**
 * Resolve collision for a solid sphere (camera must stay OUTSIDE).
 * Inflates sphere by cameraRadius, pushes camera outward along center→camera direction.
 */
export const resolveSphereCollision = (
  pos: THREE.Vector3,
  sphere: SphereColliderDef,
  cameraRadius: number,
  out: CollisionResult,
): CollisionResult => {
  const effectiveRadius = sphere.radius + cameraRadius;

  const dx = pos.x - sphere.position.x;
  const dy = pos.y - sphere.position.y;
  const dz = pos.z - sphere.position.z;
  const distSq = dx * dx + dy * dy + dz * dz;

  if (distSq < effectiveRadius * effectiveRadius) {
    const dist = Math.sqrt(distSq);
    out.position.copy(pos);
    out.normal = out.normal ?? new THREE.Vector3();

    if (dist < 1e-10) {
      out.position.y = sphere.position.y + effectiveRadius + PUSH_EPSILON;
      out.normal.set(0, 1, 0);
    } else {
      const invDist = 1 / dist;
      const nx = dx * invDist;
      const ny = dy * invDist;
      const nz = dz * invDist;
      const pushDist = effectiveRadius + PUSH_EPSILON;
      out.position.x = sphere.position.x + nx * pushDist;
      out.position.y = sphere.position.y + ny * pushDist;
      out.position.z = sphere.position.z + nz * pushDist;
      out.normal.set(nx, ny, nz);
    }

    out.collided = true;
    return out;
  }

  out.position.copy(pos);
  out.normal = null;
  out.collided = false;
  return out;
};

/**
 * Ray-sphere intersection.
 * Solves |origin + t*dir - center|² = radius².
 * Returns parametric t of first intersection in [0, maxT], or null.
 */
export const rayIntersectsSphere = (
  origin: THREE.Vector3,
  direction: THREE.Vector3,
  center: THREE.Vector3,
  radius: number,
  maxT: number,
): number | null => {
  const ox = origin.x - center.x;
  const oy = origin.y - center.y;
  const oz = origin.z - center.z;

  const a = direction.x * direction.x + direction.y * direction.y + direction.z * direction.z;
  const b = 2 * (ox * direction.x + oy * direction.y + oz * direction.z);
  const c = ox * ox + oy * oy + oz * oz - radius * radius;

  const discriminant = b * b - 4 * a * c;
  if (discriminant < 0) return null;

  const sqrtD = Math.sqrt(discriminant);
  const inv2a = 1 / (2 * a);
  const t1 = (-b - sqrtD) * inv2a;
  const t2 = (-b + sqrtD) * inv2a;

  if (t1 >= 0 && t1 <= maxT) return t1;
  if (t2 >= 0 && t2 <= maxT) return t2;

  return null;
};

/**
 * Compute slide direction: project remaining movement onto the collision plane.
 */
export const computeSlideDirection = (
  movementDir: THREE.Vector3,
  normal: THREE.Vector3,
  out: THREE.Vector3,
): THREE.Vector3 => {
  const dot = movementDir.dot(normal);
  out.copy(movementDir).addScaledVector(normal, -dot);
  return out;
};
